/* eslint-disable no-console */
import { client } from "./supabase";

const TIPOS_INTANGIBLES = ["investigacion", "software", "licencia", "patente", "marca"];

/**
 * Calcula la cuota mensual de un activo para cada mes del año dado.
 * @returns {number[]} Array de 12 elementos
 */
const calcularCuotasMensuales = (activo, year) => {
  const cuotas = Array(12).fill(0);
  const costoTotal = parseFloat(activo.costo_unitario) * activo.cantidad;
  const vidaUtilMeses = activo.vida_util_meses || 60;
  const cuotaMensual = costoTotal / vidaUtilMeses;

  const [yearCompra, monthCompra] = activo.fecha_compra.split("-").map(Number);
  const fechaCompra = new Date(yearCompra, monthCompra - 1, 1);

  for (let mes = 0; mes < 12; mes++) {
    const finMes = new Date(year, mes + 1, 0);
    if (fechaCompra > finMes) continue;

    const mesesDesdeCompra = (year - yearCompra) * 12 + mes - (monthCompra - 1);
    if (mesesDesdeCompra >= vidaUtilMeses) continue;
    if (mesesDesdeCompra < 0) continue;
    if (activo.estado !== "activo" && activo.estado !== "en_reparacion") continue;

    cuotas[mes] += Math.round(cuotaMensual);
  }

  return cuotas;
};

/**
 * Obtiene todos los activos y devuelve depreciación + amortización separadas.
 */
const fetchActivosYCalcular = async (pais, year) => {
  const { data: activos, error } = await client.from("activos").select("*").eq("pais", pais).eq("eliminado", false);

  if (error) throw error;

  const depreciacion = Array(12).fill(0);
  const amortizacion = Array(12).fill(0);

  if (!activos || activos.length === 0) {
    return { depreciacion, amortizacion };
  }

  for (const activo of activos) {
    const cuotas = calcularCuotasMensuales(activo, year);
    const esIntangible = TIPOS_INTANGIBLES.includes(activo.tipo_activo);
    const destino = esIntangible ? amortizacion : depreciacion;

    for (let mes = 0; mes < 12; mes++) {
      destino[mes] += cuotas[mes];
    }
  }

  return { depreciacion, amortizacion };
};

/**
 * Obtiene la depreciación mensual (solo activos tangibles).
 * @param {string} pais - "Chile" o "Perú"
 * @param {number} year - Año para calcular
 * @returns {Promise<number[]>} Array de 12 elementos
 */
export const getDepreciacionMensual = async (pais, year) => {
  try {
    const { depreciacion } = await fetchActivosYCalcular(pais, year);
    return depreciacion;
  } catch (error) {
    console.error("Error en getDepreciacionMensual:", error);
    return Array(12).fill(0);
  }
};

/**
 * Obtiene la amortización mensual (solo activos intangibles).
 * @param {string} pais - "Chile" o "Perú"
 * @param {number} year - Año para calcular
 * @returns {Promise<number[]>} Array de 12 elementos
 */
export const getAmortizacionMensual = async (pais, year) => {
  try {
    const { amortizacion } = await fetchActivosYCalcular(pais, year);
    return amortizacion;
  } catch (error) {
    console.error("Error en getAmortizacionMensual:", error);
    return Array(12).fill(0);
  }
};

/**
 * Obtiene depreciación y amortización en una sola llamada (evita doble fetch).
 * @param {string} pais - "Chile" o "Perú"
 * @param {number} year - Año para calcular
 * @returns {Promise<{depreciacion: number[], amortizacion: number[]}>}
 */
export const getDepreciacionYAmortizacion = async (pais, year) => {
  try {
    return await fetchActivosYCalcular(pais, year);
  } catch (error) {
    console.error("Error en getDepreciacionYAmortizacion:", error);
    return { depreciacion: Array(12).fill(0), amortizacion: Array(12).fill(0) };
  }
};
